/*import { authClient } from "../auth";
import { loginRequest } from "../authConfig";
import { useTwilioBootstrap } from "../hooks/twilio/useTwilioBootstrap";
import { clearAppStorage, wasSignedOut } from "../utils/storageUtils";
import { LoadingScreen } from "./app/LoadingScreen";
import { SignedOutScreen } from "./app/SignedOutScreen";
import { TwilioErrorScreen } from "./app/TwilioErrorScreen";
import AppContainer from "./app/AppContainer";
import { ReactElement, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { actionCreators, AppState } from "../store";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";

function App(): ReactElement {
  // Redux
  const dispatch = useDispatch();
  const { login } = bindActionCreators(actionCreators, dispatch);
  const jwt = useSelector((s: AppState) => s.token);
  const twilioToken = useSelector((s: AppState) => s.twilio.token);

  // Local state
  const [twilioError, setTwilioError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [msalReady, setMsalReady] = useState(false);
  const [signedOut] = useState<boolean>(() => wasSignedOut());

  // Derived MSAL state
  const isAuth = authClient.isAuthenticated();
  const isMsalLoading = loading || (authClient.isAuthenticated() && !jwt);
  const isTwilioLoading = authClient.isAuthenticated() && !!jwt && !twilioToken;

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        await authClient.handleRedirect();
      } catch (e) {
        console.error("[MSAL] Errore in handleRedirect:", e);
      }

      if (cancelled) return;

      if (!authClient.isAuthenticated() && !signedOut) {
        console.log("Login redirect....");
        try {
          await authClient.loginRedirect(loginRequest);
        } catch (e: any) {
          if (e?.errorCode === "interaction_in_progress") {
            console.warn(
              "[MSAL] interaction_in_progress durante bootstrap, ignoro:",
              e
            );
            return;
          }
          console.error("[MSAL] Errore in loginRedirect (bootstrap):", e);
        }
        return;
      }

      if (!cancelled) {
        setMsalReady(true);
      }
    };

    void initAuth();

    return () => {
      cancelled = true;
    };
  }, [signedOut]);

  useEffect(() => {
    if (!msalReady) return;

    const run = async () => {
      const accounts = authClient.getAccounts();
      try {
        if (!authClient.isAuthenticated()) {
          setLoading(true);
          return;
        }

        if (jwt) {
          setLoading(false);
          return;
        }

        if (!accounts.length) {
          setLoading(false);
          return;
        }

        const { accessToken } = await authClient.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        console.log("Accounts found!", accounts, accessToken);

        login(accessToken);
      } catch (e) {
        console.error(e);
        await authClient.logoutRedirect({
          account: accounts[0] as any,
          postLogoutRedirectUri: window.location.origin,
        });
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [msalReady, jwt, login]);

  useTwilioBootstrap({
    onError: (err) => {
      setTwilioError(err);
    },
  });

  const handleRetryLogin = async () => {
    setTwilioError(null);
    setLoading(true);

    if (authClient.getInProgress() !== "none") {
      console.warn(
        "[MSAL] interaction_in_progress: c'è già un login/logout in corso, non richiamo loginRedirect."
      );
      return;
    }

    clearAppStorage();
    const accounts = authClient.getAccounts();
    const account = accounts[0];

    try {
      if (authClient.isAuthenticated() && account) {
        await authClient.logoutRedirect({
          account: account as any,
          postLogoutRedirectUri: window.location.origin,
        });
      } else {
        await authClient.loginRedirect(loginRequest);
      }
    } catch (e: any) {
      if (e?.errorCode === "interaction_in_progress") {
        console.warn(
          "[MSAL] Ignoro interaction_in_progress dentro handleRetryLogin:",
          e
        );
      }

      console.error("[MSAL] Errore in handleRetryLogin:", e);
      setTwilioError(e);
      setLoading(false);
    }
  };

  if (twilioError) {
    return <TwilioErrorScreen onRetry={handleRetryLogin} />;
  }

  if (!isAuth && signedOut) {
    return <SignedOutScreen onLogin={handleRetryLogin} />;
  }

  if (!msalReady || isMsalLoading || isTwilioLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<AppContainer />} />

      <Route path="/conversations/:twilioSid" element={<AppContainer />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
*/
import { ReactElement } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useResolvedPath,
} from "react-router-dom";

import { useTwilioBootstrap } from "../hooks/twilio/useTwilioBootstrap";
import { LoadingScreen } from "./app/LoadingScreen";
import { SignedOutScreen } from "./app/SignedOutScreen";
import { TwilioErrorScreen } from "./app/TwilioErrorScreen";
import AppContainer from "./app/AppContainer";
import AdminPage from "./admin/AdminPage";
import { useAppBootstrap } from "../hooks/useAppBootstrap";
import { ConversationPage } from "./conversations/ConversationPage";
import LeadsPage from "./leads/LeadsPage";

function App(): ReactElement {
  const { screen, handleRetryLogin, onTwilioError } = useAppBootstrap();
  const location = useLocation();

  const isLead = location.pathname.indexOf("/leads") !== -1;
  if (!isLead) {
    useTwilioBootstrap({
      onError: onTwilioError,
    });

    if (screen === "twilioError")
      return <TwilioErrorScreen onRetry={handleRetryLogin} />;
    if (screen === "signedOut")
      return <SignedOutScreen onLogin={handleRetryLogin} />;
    if (screen === "loading") return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route element={!isLead ? <AppContainer /> : undefined}>
        <Route path="/" element={<ConversationPage />} />
        <Route
          path="/conversations/:twilioSid"
          element={<ConversationPage />}
        />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/leads" element={<LeadsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
